import { useState, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Form, message } from 'antd';
import { TooltipWithIcon } from 'ui';
import type { UploadFile } from 'antd';
import { Input } from '../../../Input';
import { TextArea } from '../../../TextArea';
import { DatePicker } from '../../../DatePicker';
import { Checkbox } from '../../../Checkbox';
import { Label } from 'ui/Label';
import { FileUploader } from 'ui/Upload/FileUploader';
import { WithoutModalAddressFinder } from 'ui/AdressSearch/WithoutModal';
import useQueryApiClient from '../../../../utils/useQueryApiClient';
import { useDebounce } from '../../../../utils/useDebounce';
import dayjs from 'dayjs';
import { StyledApplicationForm } from './style';
import { DateProgress } from '../../../DateProgress';

import {
  Listing,
  FarmQualityMark,
  QualityMarkId,
  ApplicationCertificate,
  ListingProduct,
  ApplicationDeliveryCondition,
} from '../../../../types/ListingData';
import { Divider } from 'ui/Divider';
import { routes } from '../../../../config/config';
import {
  QualityMarkOption,
  findQualityMark,
  hasRequiredQualityMark,
} from 'utils/qualityMarkHelpers';

interface CustomUploadFile extends UploadFile {
  file_path?: string;
  response?: {
    file_path?: string;
  };
  fileId?: number;
  created_at?: string;
}

interface NewApplicationFormProps {
  listingData?: Listing | null;
  isEditing?: boolean;
  farmQualityMarks?: FarmQualityMark[];
  initialFarmQualityMarkIds?: QualityMarkId[];
  onCertificatesChange?: (certificates: ApplicationCertificate[]) => void;
  initialCertificates?: ApplicationCertificate[];
  applicationId?: number | null;
  farmData?: {
    warehouse_address?: string;
    lks_lat?: number;
    lks_long?: number;
  };
  allQualityMarks?: QualityMarkOption[];
  hasProductsMissingQualityMarks?: boolean;
  ignoreDeadlineValidation?: boolean;
}

export const NewApplicationForm = ({
  listingData,
  isEditing = false,
  farmQualityMarks = [],
  initialFarmQualityMarkIds = [],
  onCertificatesChange,
  initialCertificates,
  applicationId,
  farmData,
  allQualityMarks,
  hasProductsMissingQualityMarks = false,
  ignoreDeadlineValidation = false,
}: NewApplicationFormProps) => {
  const intl = useIntl();
  const form = Form.useFormInstance();
  const [certificates, setCertificates] = useState<ApplicationCertificate[]>(
    []
  );
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [calculatedDistanceKm, setCalculatedDistanceKm] = useState<
    number | null
  >(null);

  const lksLat = Form.useWatch('lks_lat', form);
  const lksLong = Form.useWatch('lks_long', form);
  const debouncedLat = useDebounce(lksLat, 400);
  const debouncedLong = useDebounce(lksLong, 400);

  const { appendData } = useQueryApiClient({
    request: {
      url: 'api/v2/listings/:listingId/calculate-distance',
      disableOnMount: true,
    },
    onSuccess: (response: { distance_km?: number | null }) => {
      setCalculatedDistanceKm(response?.distance_km ?? null);
    },
    enabled: !!listingData?.id,
  });

  useEffect(() => {
    if (Array.isArray(initialCertificates) && initialCertificates.length > 0) {
      setCertificates((prev) => {
        const prevIds = new Set(
          prev.map((c) => c.file_id || c.id).filter(Boolean)
        );
        const newIds = new Set(
          initialCertificates.map((c) => c.file_id || c.id).filter(Boolean)
        );
        const idsMatch =
          prevIds.size === newIds.size &&
          Array.from(prevIds).every((id) => newIds.has(id));

        if (idsMatch) {
          return prev;
        }

        return initialCertificates;
      });

      const existingFiles: UploadFile[] = initialCertificates.map((c) => {
        const fileObj: any = (c as any).file;
        const name =
          c.file_name ||
          fileObj?.original_name ||
          fileObj?.name ||
          `certificate_${c.file_id || ''}`;
        const size = c.file_size || fileObj?.size_bytes;
        return {
          uid: `existing-${c.file_id || c.id || Math.random()}`,
          name,
          status: 'done',
          size,
          file_path: fileObj?.url,
          fileId: c.file_id,
          created_at: c.created_at || fileObj?.created_at,
        } as UploadFile;
      });
      setFileList(existingFiles);
    } else if (
      Array.isArray(initialCertificates) &&
      initialCertificates.length === 0
    ) {
      setCertificates((prev) => (prev.length === 0 ? prev : []));
      setFileList([]);
    }
  }, [initialCertificates]);

  const offerValidFrom = Form.useWatch('offer_valid_from');
  const deliveryConditions = Form.useWatch('delivery_conditions', form) as
    | ApplicationDeliveryCondition[]
    | undefined;

  const totalDeliveryCost = useMemo(() => {
    if (!deliveryConditions || !Array.isArray(deliveryConditions)) {
      return 0;
    }

    return deliveryConditions.reduce((total, condition) => {
      const price = condition.delivery_price;
      if (price !== null && price !== undefined && !isNaN(Number(price))) {
        return total + Number(price);
      }
      return total;
    }, 0);
  }, [deliveryConditions]);

  useEffect(() => {
    if (form && totalDeliveryCost !== undefined) {
      form.setFieldValue('delivery_cost', totalDeliveryCost.toFixed(2));
    }
  }, [form, totalDeliveryCost]);

  useEffect(() => {
    if (
      farmData?.warehouse_address &&
      farmData.lks_lat != null &&
      farmData.lks_long != null &&
      form
    ) {
      const currentAddress = form.getFieldValue('warehouse_address');
      if (!currentAddress) {
        form.setFieldsValue({
          warehouse_address: farmData.warehouse_address,
          lks_lat: farmData.lks_lat,
          lks_long: farmData.lks_long,
        });
      }
    }
  }, [farmData, form]);

  useEffect(() => {
    if (
      listingData?.id &&
      listingData?.lks_lat != null &&
      listingData?.lks_long != null &&
      debouncedLat != null &&
      debouncedLong != null &&
      !isNaN(Number(debouncedLat)) &&
      !isNaN(Number(debouncedLong))
    ) {
      appendData(
        { lat: debouncedLat, long: debouncedLong },
        { listingId: String(listingData.id) }
      );
    } else {
      setCalculatedDistanceKm(null);
    }
  }, [
    listingData?.id,
    listingData?.lks_lat,
    listingData?.lks_long,
    debouncedLat,
    debouncedLong,
  ]);

  const listingQualityMarks = useMemo(() => {
    if (!listingData?.products || !allQualityMarks) return [];

    const qualityMarksMap = new Map<string, QualityMarkOption>();

    listingData.products.forEach((product: ListingProduct) => {
      if (product.quality_mark_ids && Array.isArray(product.quality_mark_ids)) {
        product.quality_mark_ids.forEach((id) => {
          const qm = findQualityMark(allQualityMarks, id);
          if (qm && !qualityMarksMap.has(String(id))) {
            qualityMarksMap.set(String(id), { id: qm.id, name: qm.name });
          }
        });
      }
    });

    return Array.from(qualityMarksMap.values());
  }, [listingData?.products, allQualityMarks]);

  const farmQualityMarkIds = useMemo(() => {
    return initialFarmQualityMarkIds;
  }, [initialFarmQualityMarkIds]);

  const qualityMarkOptions = useMemo(() => {
    return listingQualityMarks.map((qmark) => {
      const hasFarmQualityMark = hasRequiredQualityMark(
        qmark.id,
        farmQualityMarkIds,
        allQualityMarks
      );
      return {
        value: qmark.id,
        label: qmark.name,
        disabled: true,
        checked: true,
        className: hasFarmQualityMark ? 'has-quality-mark' : 'no-quality-mark',
      };
    });
  }, [listingQualityMarks, farmQualityMarkIds, allQualityMarks]);

  const isUploading = false;

  const handleFileListChange = (newFileList: UploadFile[]) => {
    setFileList(newFileList);

    setCertificates((prev) => {
      const existingFileIds = new Set(
        prev.map((c) => c.file_id).filter(Boolean)
      );
      const existingFileNames = new Set(
        prev.map((c) => c.file_name?.toLowerCase()).filter(Boolean)
      );

      const pendingCertificates: ApplicationCertificate[] = newFileList
        .filter((f) => {
          if (!(f as any).originFileObj) {
            return false;
          }

          const fileId = (f as any).fileId;
          const fileName = f.name?.toLowerCase();

          if (fileId && existingFileIds.has(fileId)) {
            return false;
          }

          if (fileName && existingFileNames.has(fileName)) {
            return false;
          }

          return true;
        })
        .map((file) => ({
          id: Number(file.uid) || Date.now(),
          file_name: file.name,
          file_size: file.size,
          file_format: (file.name?.split('.').pop() || '').toLowerCase(),
          created_at: new Date().toISOString(),
          originFile: (file as any).originFileObj,
          isPending: true,
        }));

      const updated = [...prev, ...pendingCertificates];
      onCertificatesChange?.(updated);

      if (hasProductsMissingQualityMarks) {
        const validCertificates = updated.filter(
          (c) => !c.isPending || c.file_id || c.originFile
        );
        if (validCertificates.length > 0) {
          setTimeout(() => {
            form.setFields([
              {
                name: 'certificates',
                errors: [],
              },
            ]);
          }, 0);
        } else {
          setTimeout(() => {
            form.validateFields(['certificates']).catch(() => {});
          }, 0);
        }
      }

      return updated;
    });
  };

  const handleFileRemove = (file: UploadFile) => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(newFileList);

    const existingFileId = (file as any).fileId as number | undefined;
    setCertificates((prev) => {
      const updated = prev.filter((cert) => {
        if (existingFileId && cert.file_id) {
          return cert.file_id !== existingFileId;
        }
        const fileObj: any = (cert as any).file;
        const name = cert.file_name || fileObj?.original_name || fileObj?.name;
        return name !== file.name;
      });

      onCertificatesChange?.(updated);

      if (hasProductsMissingQualityMarks) {
        const validCertificates = updated.filter(
          (c) => !c.isPending || c.file_id || c.originFile
        );
        if (validCertificates.length > 0) {
          setTimeout(() => {
            form.setFields([
              {
                name: 'certificates',
                errors: [],
              },
            ]);
          }, 0);
        } else {
          setTimeout(() => {
            form.validateFields(['certificates']).catch(() => {});
          }, 0);
        }
      }

      return updated;
    });
  };

  const handleFileDownload = (file: UploadFile) => {
    const f = file as CustomUploadFile;
    const appId = applicationId;

    if (appId && f.fileId) {
      const url = `${routes.api.baseUrl}/api/v2/listing-applications/${appId}/certificates/download?file_id=${f.fileId}`;
      window.open(url, '_blank');
      return;
    }

    if (appId && f.file_path) {
      const url = `${routes.api.baseUrl}/api/v2/listing-applications/${appId}/certificates/download?file=${encodeURIComponent(
        f.file_path
      )}`;
      window.open(url, '_blank');
      return;
    }

    const matched = certificates.find((c) => {
      const name = c.file_name || c.file?.original_name || c.file?.name;
      return (f.fileId && c.file_id === f.fileId) || name === file.name;
    });
    const storedPath = matched?.file?.url;

    if (storedPath) {
      window.open(`${routes.api.baseUrl}/storage/${storedPath}`, '_blank');
      return;
    }

    const blob = (file as any).originFileObj as File | undefined;
    if (blob) {
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(objectUrl);
      return;
    }

    message.warning(
      intl.formatMessage({ id: 'file_uploader.download_unavailable' })
    );
  };

  return (
    <StyledApplicationForm>
      <div className="listing-info">
        <div className="listing-date hide-mobile">
          {listingData?.publication_end_date && (
            <div className="deadline-progress">
              <Label
                label={intl.formatMessage({
                  id: 'listings.application.publication_start_date',
                })}
              />
              <div className="deadline-date">
                {dayjs(listingData.publication_start_date).format('DD.MM.YYYY')}
              </div>
              {listingData.publication_start_date &&
                listingData.publication_end_date && (
                  <DateProgress
                    startDate={listingData.publication_start_date}
                    endDate={listingData.publication_end_date}
                  />
                )}

              <Label
                label={intl.formatMessage({
                  id: 'listings.application.publication_end_date',
                })}
              />
              <div className="deadline-date">
                {dayjs(listingData.publication_end_date).format('DD.MM.YYYY')}
              </div>
            </div>
          )}
        </div>
        <div className="listing-info hide-mobile">
          <Label
            label={listingData?.farm_name || listingData?.farm_brand || ''}
            title={true}
          />
        </div>
        <div className="listing-info">
          <Label
            label={intl.formatMessage({
              id: 'listings.application.listing_info',
            })}
            title={true}
          />
          <p>{listingData?.description}</p>
        </div>
        <Divider />
        <div>
          <Label
            label={`${intl.formatMessage({
              id: 'listings.application.Delivery_radius',
            })}${calculatedDistanceKm != null ? ` (${calculatedDistanceKm} km)` : ''}`}
            title={true}
          />
          <p>{listingData?.delivery_radius_km} km</p>
        </div>
      </div>

      <Divider />
      {/* Producer Comments */}
      <div className="producer-comments">
        <Label
          label={intl.formatMessage({
            id: 'listings.application.producer_comment',
          })}
          title={true}
        />
        <TextArea
          className="textarea"
          name="producer_comments"
          placeholder={intl.formatMessage({
            id: 'listings.application.producer_comments_placeholder',
          })}
          maxLength={2000}
          autoSize={true}
        />
      </div>

      <Divider />
      {/* Offer Validity Period */}
      <div className="offer-validity-period">
        <Label
          label={intl.formatMessage({
            id: 'listings.application.offer_validity_period',
          })}
          title={true}
        />
        <div className="row">
          <div className="date">
            <DatePicker
              name="offer_valid_from"
              label={intl.formatMessage({
                id: 'listings.application.start_date',
              })}
              showTime={{ minuteStep: 10 }}
              needConfirm={false}
              format="DD.MM.YYYY HH:mm"
              placeholder={intl.formatMessage({
                id: 'listings.application.start_date_placeholder',
              })}
              disabledDate={
                ignoreDeadlineValidation
                  ? undefined
                  : (current) => {
                      if (!current) return false;
                      if (listingData?.publication_start_date) {
                        const listingStart = dayjs(
                          listingData.publication_start_date
                        );
                        if (current.isBefore(listingStart, 'day')) {
                          return true;
                        }
                      }
                      return false;
                    }
              }
              rules={
                ignoreDeadlineValidation
                  ? []
                  : [
                      {
                        validator: async (_, value) => {
                          if (value && listingData?.publication_start_date) {
                            const offerValidFrom = dayjs(value);
                            const listingStartDate = dayjs(
                              listingData.publication_start_date
                            );
                            if (offerValidFrom.isBefore(listingStartDate)) {
                              return Promise.reject(
                                new Error(
                                  intl.formatMessage({
                                    id: 'validation.offer_valid_from_before_listing_start',
                                  })
                                )
                              );
                            }
                          }
                          return Promise.resolve();
                        },
                      },
                    ]
              }
            />
          </div>
          <div className="date">
            <DatePicker
              name="offer_valid_until"
              label={intl.formatMessage({
                id: 'listings.application.end_date',
              })}
              needConfirm={false}
              format="DD.MM.YYYY"
              placeholder={intl.formatMessage({
                id: 'listings.application.end_date_placeholder',
              })}
              disabledDate={
                ignoreDeadlineValidation
                  ? undefined
                  : (current) => {
                      if (!current) return false;
                      if (
                        current &&
                        offerValidFrom &&
                        current.isBefore(dayjs(offerValidFrom), 'day')
                      ) {
                        return true;
                      }
                      return false;
                    }
              }
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const startDate = getFieldValue('offer_valid_from');
                    if (!value || !startDate) {
                      return Promise.resolve();
                    }
                    const endDate = dayjs(value).endOf('day');
                    const start = dayjs(startDate);
                    if (endDate.isBefore(start)) {
                      return Promise.reject(
                        new Error(
                          intl.formatMessage({
                            id: 'validation.end_date_after_start',
                          })
                        )
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            />
          </div>
        </div>
      </div>

      <Divider />

      {/* Warehouse Address */}
      <div className="warehouse-address">
        <Label
          label={intl.formatMessage({ id: 'listings.form.delivery_address' })}
          title={true}
        />
        <WithoutModalAddressFinder
          label={
            <span>
              {intl.formatMessage({ id: 'general.address' })}
              <TooltipWithIcon
                title={intl.formatMessage({
                  id: 'listings.application.delivery_address_note',
                })}
              />
            </span>
          }
          form={form}
          name="warehouse_address"
          disabled={false}
          deliveryTypeValue="delivery"
          validations="required"
          requireCoordinates={true}
        />
      </div>

      {/* Total Delivery Cost */}
      <div className="total-delivery-cost">
        <Label
          label={intl.formatMessage({
            id: 'listings.application.total_delivery_cost',
          })}
          title={true}
        />
        <Input
          name="delivery_cost"
          label={intl.formatMessage({
            id: 'general.EUR',
          })}
          type="number"
          placeholder="0.00"
          readOnly={true}
          initialValue={totalDeliveryCost.toFixed(2)}
        />
      </div>

      <Divider />

      {/* Quality Marks */}
      {qualityMarkOptions.length > 0 && (
        <div className="quality-marks">
          <Label
            label={intl.formatMessage({
              id: 'listings.application.quality_marks',
            })}
            title={true}
          />
          <div className="quality-mark-items">
            {qualityMarkOptions.map((qmarkOption) => (
              <div key={qmarkOption.value} className="quality-mark-item">
                <Checkbox
                  checked={qmarkOption.checked}
                  disabled={qmarkOption.disabled}
                  className={qmarkOption.className}
                />
                <Label
                  className="quality-mark-label"
                  label={qmarkOption.label}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Product Certificates */}
      <div className="product-certificates">
        <Label
          label={intl.formatMessage({
            id: 'listings.application.product_certificates',
          })}
          title={true}
          required={hasProductsMissingQualityMarks}
        />
        <Form.Item
          name="certificates"
          shouldUpdate={(prevValues, currentValues) => {
            return true;
          }}
          rules={[
            {
              validator: async () => {
                if (hasProductsMissingQualityMarks) {
                  const validCertificates = certificates.filter(
                    (c) => !c.isPending || c.file_id || c.originFile
                  );
                  if (validCertificates.length === 0) {
                    return Promise.reject(
                      new Error(
                        intl.formatMessage({
                          id: 'validation.product_missing_quality_marks_warning',
                        })
                      )
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <FileUploader
            name="certificates"
            fileList={fileList}
            onFileListChange={handleFileListChange}
            acceptedTypes={['.pdf', '.xlsx', '.docx']}
            multiple={true}
            maxFiles={listingData?.products?.length || 10}
            disabled={isUploading}
            onDownload={handleFileDownload}
            onDelete={handleFileRemove}
          />
        </Form.Item>
      </div>
    </StyledApplicationForm>
  );
};
